import React, {
  useState,
  createContext,
  useContext,
  useCallback,
  FC,
  ReactNode,
} from "react";

// Error Modal Hook
type ErrorModalType = {
  isErrorModalOpen: boolean;
  errorModalMessage: string | null;
  showErrorModal: (message?: string) => void;
  hideErrorModal: () => void;
};
const useErrorModal = (): ErrorModalType => {
  const [errorModalMessage, setErrorModalMessage] = useState<string | null>(
    null
  );

  const showErrorModal = useCallback((message?: string) => {
    setErrorModalMessage(message || null);
  }, []);

  const hideErrorModal = useCallback(() => {
    setErrorModalMessage(null);
  }, []);

  return {
    isErrorModalOpen: !!errorModalMessage,
    errorModalMessage,
    showErrorModal,
    hideErrorModal,
  };
};

// Signup Error Modal Hook
type SignupErrorModalType = {
  isSignupErrorModalOpen: boolean;
  signupErrorModalType: string | null;
  signupErrorModalMessage: string | null;
  showSignupErrorModal: (type: string, message?: string) => void;
  hideSignupErrorModal: () => void;
};
const useSignupErrorModal = (): SignupErrorModalType => {
  const [signupErrorModalType, setSignupErrorModalType] = useState<
    string | null
  >(null);
  const [signupErrorModalMessage, setSignupErrorModalMessage] = useState<
    string | null
  >(null);

  const showSignupErrorModal = useCallback((type: string, message?: string) => {
    setSignupErrorModalType(type);
    setSignupErrorModalMessage(message || null);
  }, []);

  const hideSignupErrorModal = useCallback(() => {
    setSignupErrorModalType(null);
    setSignupErrorModalMessage(null);
  }, []);

  return {
    isSignupErrorModalOpen: !!signupErrorModalType,
    signupErrorModalType,
    signupErrorModalMessage,
    showSignupErrorModal,
    hideSignupErrorModal,
  };
};

// SignUp Modal Hook
type SignupModalType = {
  isSignUpModalOpen: boolean;
  showSignUpModal: () => void;
  hideSignUpModal: () => void;
};
const useSignUpModal = (): SignupModalType => {
  const [isSignUpModalOpen, setSignUpModalOpen] = useState(false);

  const showSignUpModal = useCallback(() => {
    setSignUpModalOpen(true);
  }, []);

  const hideSignUpModal = useCallback(() => {
    setSignUpModalOpen(false);
  }, []);

  return { isSignUpModalOpen, showSignUpModal, hideSignUpModal };
};

// Signup Complete Modal Hook
type SignupCompleteModalType = {
  isSignupCompleteModalOpen: boolean;
  showSignupCompleteModal: (callbackOnClose?: () => void) => void;
  hideSignupCompleteModal: () => void;
};
const useSignupCompleteModal = (): SignupCompleteModalType => {
  const [isSignupCompleteModalOpen, setSignupCompleteModalOpen] =
    useState(false);
  const [signupCompleteCloseCallback, setSignupCompleteCloseCallback] =
    useState<() => void | undefined>();

  const showSignupCompleteModal = useCallback(
    (callbackOnClose?: () => void) => {
      setSignupCompleteCloseCallback(() => callbackOnClose);
      setSignupCompleteModalOpen(true);
    },
    []
  );

  const hideSignupCompleteModal = useCallback(() => {
    if (signupCompleteCloseCallback) {
      signupCompleteCloseCallback();
    }
    setSignupCompleteModalOpen(false);
  }, [signupCompleteCloseCallback]);

  return {
    isSignupCompleteModalOpen,
    showSignupCompleteModal,
    hideSignupCompleteModal,
  };
};

// SignUp Modal Hook
type LightboxModalType = {
  isLightboxOpen: boolean;
  lightboxKey?: string;
  showLightbox: (lightboxKey: string) => void;
  hideLightbox: () => void;
};
const useLightboxModal = (): LightboxModalType => {
  const [lightboxKey, setModalOpen] = useState<string | undefined>();
  const isLightboxOpen = Boolean(lightboxKey);
  const showLightbox = useCallback((lightboxKey: string) => {
    setModalOpen(lightboxKey);
  }, []);

  const hideLightbox = useCallback(() => {
    setModalOpen(undefined);
  }, []);
  console.log(showLightbox);
  return {
    isLightboxOpen,
    lightboxKey,
    showLightbox,
    hideLightbox,
  };
};

type ModalContextType = ErrorModalType &
  SignupErrorModalType &
  SignupModalType &
  SignupCompleteModalType &
  LightboxModalType;

// Modal Provider
export const ModalContext = createContext<ModalContextType>(
  {} as ModalContextType
);
export const useModal = () => useContext(ModalContext);

export const ModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const errorModal = useErrorModal();
  const signupErrorModal = useSignupErrorModal();
  const signUpModal = useSignUpModal();
  const signupCompleteModal = useSignupCompleteModal();
  const lightboxModal = useLightboxModal();

  return (
    <ModalContext.Provider
      value={{
        ...errorModal,
        ...signupErrorModal,
        ...signUpModal,
        ...signupCompleteModal,
        ...lightboxModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};
